/**
 * Unsaved Changes Detection Utilities
 *
 * Provides functions to capture original state and compare current form data
 * to detect unsaved changes in Item and SubItem editors.
 */

import type { ItemType, Item, SubItem, Reminder } from '@perfin/shared';
import type { OriginalState, ItemDraft, SubItemDraft, ChildOrderEntry } from '@/services';
import type { ItemFormData } from '../components/fields';
import { formDataToRecord } from './formHelpers';

// ============================================================================
// TYPES
// ============================================================================

export interface UnsavedChangesResult {
  hasChanges: boolean;
  changedFields: string[];
  hasAddedAttachments: boolean;
  hasRemovedAttachments: boolean;
  hasAddedSubItems: boolean;
  hasRemovedSubItems: boolean;
  hasReorderedChildren: boolean;
  summary: string;
}

// Fields relevant to each ITEM type (used for type-aware comparison)
// IMPORTANT: Must include ALL fields from ItemFormData that apply to each type
// Items have: itemGroup, recurrence fields, choreWheel (task only)
const ITEM_TYPE_FIELDS: Record<ItemType, string[]> = {
  task: [
    'name', 'description', 'category', 'subCategory', 'itemGroup', 'owner', 'priority', 'effortHours',
    'dueDate', 'startDate', 'endDate', 'endDateForever', 'recurrence', 'daysOfWeek', 'daysOfMonth', 'datesOfYear',
    'status', 'choreWheel', 'urls', 'reminders',
  ],
  transaction: [
    'name', 'description', 'category', 'subCategory', 'itemGroup', 'owner', 'priority', 'effortHours',
    'dueDate', 'startDate', 'endDate', 'endDateForever', 'recurrence', 'daysOfWeek', 'daysOfMonth', 'datesOfYear',
    'status', 'transactionType', 'amount', 'currency', 'entityId', 'accountId',
    'urls', 'reminders',
  ],
  note: [
    'name', 'description', 'category', 'subCategory', 'itemGroup', 'owner', 'priority', 'effortHours',
    'dueDate',
    'status', 'content', 'color', 'tags',
    'urls', 'reminders',
  ],
  keyDate: [
    'name', 'description', 'category', 'subCategory', 'itemGroup', 'owner', 'priority', 'effortHours',
    'dueDate', 'startDate', 'endDate', 'endDateForever', 'recurrence', 'daysOfWeek', 'daysOfMonth', 'datesOfYear',
    'status',
    'reminders',
  ],
};

// Fields relevant to each SUBITEM type (no itemGroup, no recurrence, no choreWheel)
// Sub-items only have: dueDate (one-time), no recurrence fields
const SUBITEM_TYPE_FIELDS: Record<ItemType, string[]> = {
  task: [
    'name', 'description', 'category', 'subCategory', 'owner', 'priority', 'effortHours',
    'dueDate', 'status', 'urls', 'reminders',
  ],
  transaction: [
    'name', 'description', 'category', 'subCategory', 'owner', 'priority', 'effortHours',
    'dueDate', 'status', 'transactionType', 'amount', 'currency', 'entityId', 'accountId',
    'urls', 'reminders',
  ],
  note: [
    'name', 'description', 'category', 'subCategory', 'owner', 'priority', 'effortHours',
    'dueDate', 'status', 'content', 'color', 'tags',
    'urls', 'reminders',
  ],
  keyDate: [
    'name', 'description', 'category', 'subCategory', 'owner', 'priority', 'effortHours',
    'dueDate', 'status',
    'reminders',
  ],
};

// ============================================================================
// CAPTURE ORIGINAL STATE
// ============================================================================

/**
 * Deep clone an object (handles Date objects and arrays)
 */
function deepClone<T>(obj: T): T {
  if (obj === null || obj === undefined) return obj;
  if (obj instanceof Date) return new Date(obj.getTime()) as unknown as T;
  if (Array.isArray(obj)) return obj.map(item => deepClone(item)) as unknown as T;
  if (typeof obj === 'object') {
    const cloned: Record<string, unknown> = {};
    for (const key in obj) {
      if (Object.prototype.hasOwnProperty.call(obj, key)) {
        cloned[key] = deepClone((obj as Record<string, unknown>)[key]);
      }
    }
    return cloned as T;
  }
  return obj;
}

/**
 * Build child order array from attachments and sub-items, sorted by order field
 */
export function buildChildOrder(
  attachments: Array<{ id: string; order?: number }>,
  subItems: Array<{ id: string; order?: number }>
): ChildOrderEntry[] {
  const items: Array<{ type: 'attachment' | 'subItem'; id: string; order: number }> = [];

  for (const a of attachments) {
    items.push({ type: 'attachment', id: a.id, order: a.order ?? 999 });
  }
  for (const s of subItems) {
    items.push({ type: 'subItem', id: s.id, order: s.order ?? 999 });
  }

  items.sort((a, b) => a.order - b.order);

  return items.map(({ type, id }) => ({ type, id }));
}

/**
 * Capture original state for an Item (edit mode)
 * Note: Sub-items are stored in a separate collection, so we don't track their IDs here.
 * The draft.pendingSubItems array is used to track new sub-items instead.
 * @param subItems - Optional array of sub-items for order tracking (pass when available)
 */
export function captureOriginalStateFromItem(
  formData: ItemFormData,
  item: Item,
  subItems?: SubItem[]
): OriginalState {
  const attachments = item.attachments || [];
  const subs = subItems || [];

  return {
    formData: deepClone(formDataToRecord(formData)),
    existingAttachmentIds: attachments.map(a => a.id),
    existingSubItemIds: [], // Sub-items tracked separately via pendingSubItems
    originalChildOrder: buildChildOrder(attachments, subs),
    capturedAt: Date.now(),
  };
}

/**
 * Capture original state for a SubItem (edit mode)
 */
export function captureOriginalStateFromSubItem(
  formData: ItemFormData,
  subItem: SubItem
): OriginalState {
  return {
    formData: deepClone(formDataToRecord(formData)),
    existingAttachmentIds: (subItem.attachments || []).map(a => a.id),
    existingSubItemIds: [],
    capturedAt: Date.now(),
  };
}

/**
 * Capture original state for new item creation
 */
export function captureOriginalStateFromDefaults(
  formData: ItemFormData
): OriginalState {
  return {
    formData: deepClone(formDataToRecord(formData)),
    existingAttachmentIds: [],
    existingSubItemIds: [],
    capturedAt: Date.now(),
  };
}

// ============================================================================
// COMPARISON UTILITIES
// ============================================================================

/**
 * Check if a string looks like an ISO date string
 */
function isDateString(value: string): boolean {
  // ISO 8601 format: 2024-03-15T00:00:00.000Z
  return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(value);
}

/**
 * Normalize a value for comparison:
 * - Trim strings and treat empty string as equivalent to null/undefined
 * - Treat empty arrays as equivalent
 * - Convert Date objects and date strings to timestamps
 */
function normalizeValue(value: unknown): unknown {
  if (value === null || value === undefined) return null;
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') return null;
    // Convert date strings to timestamps for comparison
    if (isDateString(trimmed)) {
      return new Date(trimmed).getTime();
    }
    return trimmed;
  }
  if (value instanceof Date) return value.getTime();
  if (Array.isArray(value)) {
    if (value.length === 0) return null;
    return value.map(normalizeValue);
  }
  if (typeof value === 'object') {
    // Handle Firestore Timestamp format
    if ('seconds' in value && typeof (value as { seconds: unknown }).seconds === 'number') {
      return (value as { seconds: number }).seconds * 1000;
    }
    const normalized: Record<string, unknown> = {};
    for (const key in value) {
      if (Object.prototype.hasOwnProperty.call(value, key)) {
        normalized[key] = normalizeValue((value as Record<string, unknown>)[key]);
      }
    }
    return normalized;
  }
  return value;
}

/**
 * Deep compare two values
 */
function deepEqual(a: unknown, b: unknown): boolean {
  const normA = normalizeValue(a);
  const normB = normalizeValue(b);

  if (normA === normB) return true;
  if (normA === null || normB === null) return normA === normB;

  if (typeof normA !== typeof normB) return false;

  if (Array.isArray(normA) && Array.isArray(normB)) {
    if (normA.length !== normB.length) return false;
    for (let i = 0; i < normA.length; i++) {
      if (!deepEqual(normA[i], normB[i])) return false;
    }
    return true;
  }

  if (typeof normA === 'object' && typeof normB === 'object') {
    const keysA = Object.keys(normA as Record<string, unknown>);
    const keysB = Object.keys(normB as Record<string, unknown>);
    if (keysA.length !== keysB.length) return false;
    for (const key of keysA) {
      if (!deepEqual(
        (normA as Record<string, unknown>)[key],
        (normB as Record<string, unknown>)[key]
      )) return false;
    }
    return true;
  }

  return false;
}

/**
 * Compare child order arrays
 * Returns true if orders are equal
 */
function compareChildOrder(
  original: ChildOrderEntry[] | undefined,
  current: ChildOrderEntry[] | undefined
): boolean {
  // Both undefined or empty = equal
  if (!original && !current) return true;
  if (!original || !current) return false;
  if (original.length !== current.length) return false;

  for (let i = 0; i < original.length; i++) {
    if (original[i].type !== current[i].type || original[i].id !== current[i].id) {
      return false;
    }
  }
  return true;
}

/**
 * Compare reminders arrays (special handling for reminder objects)
 * Reminder has: id, daysBefore, time?, sent?
 */
function compareReminders(original: Reminder[] | undefined, current: Reminder[] | undefined): boolean {
  const origArr = original || [];
  const currArr = current || [];

  if (origArr.length !== currArr.length) return false;

  for (let i = 0; i < origArr.length; i++) {
    const a = origArr[i];
    const b = currArr[i];
    if (a.id !== b.id) return false;
    if (a.daysBefore !== b.daysBefore) return false;
    if ((a.time || '') !== (b.time || '')) return false;
  }
  return true;
}

/**
 * Compare form data field by field, type-aware
 * @param isSubItem - If true, use sub-item fields (no recurrence, no itemGroup, no choreWheel)
 */
export function compareFormData(
  original: Record<string, unknown>,
  current: Record<string, unknown>,
  type: ItemType,
  isSubItem: boolean = false
): string[] {
  const changedFields: string[] = [];
  const fieldsToCheck = isSubItem ? SUBITEM_TYPE_FIELDS[type] : ITEM_TYPE_FIELDS[type];

  for (const field of fieldsToCheck) {
    const origValue = original[field];
    const currValue = current[field];

    // Special handling for reminders
    if (field === 'reminders') {
      if (!compareReminders(origValue as Reminder[] | undefined, currValue as Reminder[] | undefined)) {
        changedFields.push(field);
      }
      continue;
    }

    if (!deepEqual(origValue, currValue)) {
      changedFields.push(field);
    }
  }

  return changedFields;
}

// ============================================================================
// MAIN DETECTION FUNCTION
// ============================================================================

/**
 * Detect unsaved changes in an ItemDraft
 */
export function hasUnsavedChanges(
  draft: ItemDraft,
  currentFormData: ItemFormData,
  pendingAttachmentCount: number = 0
): UnsavedChangesResult {
  const hasAddedAttachments = pendingAttachmentCount > 0;
  const hasRemovedAttachments = draft.removedAttachmentIds?.length > 0;
  const hasAddedSubItems = draft.pendingSubItems?.length > 0;
  const hasRemovedSubItems = draft.removedSubItemIds?.length > 0;

  // Check for reorder changes
  const hasReorderedChildren = draft.reorderedChildren !== undefined &&
    !compareChildOrder(draft.originalState?.originalChildOrder, draft.reorderedChildren);

  // If no original state captured, still check for pending attachments/sub-items/reorder
  if (!draft.originalState) {
    const hasChanges = hasAddedAttachments || hasRemovedAttachments || hasAddedSubItems || hasRemovedSubItems || hasReorderedChildren;
    const summaryParts: string[] = [];
    if (hasAddedAttachments) summaryParts.push('attachments to upload');
    if (hasRemovedAttachments) summaryParts.push('attachments to remove');
    if (hasAddedSubItems) summaryParts.push('sub-items to add');
    if (hasRemovedSubItems) summaryParts.push('sub-items to remove');
    if (hasReorderedChildren) summaryParts.push('items reordered');

    return {
      hasChanges,
      changedFields: [],
      hasAddedAttachments,
      hasRemovedAttachments,
      hasAddedSubItems,
      hasRemovedSubItems,
      hasReorderedChildren,
      summary: summaryParts.join(', '),
    };
  }

  const currentRecord = formDataToRecord(currentFormData);
  const changedFields = compareFormData(
    draft.originalState.formData,
    currentRecord,
    draft.currentType
  );

  const hasChanges = changedFields.length > 0 ||
    hasAddedAttachments ||
    hasRemovedAttachments ||
    hasAddedSubItems ||
    hasRemovedSubItems ||
    hasReorderedChildren;

  // Build summary
  const summaryParts: string[] = [];
  if (changedFields.length > 0) {
    summaryParts.push(`${changedFields.length} field${changedFields.length > 1 ? 's' : ''} modified`);
  }
  if (hasAddedAttachments) {
    summaryParts.push('attachments to upload');
  }
  if (hasRemovedAttachments) {
    summaryParts.push('attachments to remove');
  }
  if (hasAddedSubItems) {
    summaryParts.push('sub-items to add');
  }
  if (hasRemovedSubItems) {
    summaryParts.push('sub-items to remove');
  }
  if (hasReorderedChildren) {
    summaryParts.push('items reordered');
  }

  return {
    hasChanges,
    changedFields,
    hasAddedAttachments,
    hasRemovedAttachments,
    hasAddedSubItems,
    hasRemovedSubItems,
    hasReorderedChildren,
    summary: summaryParts.join(', '),
  };
}

/**
 * Detect unsaved changes in a SubItemDraft
 */
export function hasSubItemUnsavedChanges(
  draft: SubItemDraft,
  currentFormData: ItemFormData,
  pendingAttachmentCount: number = 0
): UnsavedChangesResult {
  const hasAddedAttachments = pendingAttachmentCount > 0;
  const hasRemovedAttachments = draft.removedAttachmentIds?.length > 0;

  // If no original state captured, still check for pending attachments
  if (!draft.originalState) {
    const hasChanges = hasAddedAttachments || hasRemovedAttachments;
    const summaryParts: string[] = [];
    if (hasAddedAttachments) summaryParts.push('attachments to upload');
    if (hasRemovedAttachments) summaryParts.push('attachments to remove');

    return {
      hasChanges,
      changedFields: [],
      hasAddedAttachments,
      hasRemovedAttachments,
      hasAddedSubItems: false,
      hasRemovedSubItems: false,
      hasReorderedChildren: false,
      summary: summaryParts.join(', '),
    };
  }

  const currentRecord = formDataToRecord(currentFormData);
  const changedFields = compareFormData(
    draft.originalState.formData,
    currentRecord,
    draft.currentType,
    true // isSubItem = true
  );

  const hasChanges = changedFields.length > 0 || hasAddedAttachments || hasRemovedAttachments;

  // Build summary
  const summaryParts: string[] = [];
  if (changedFields.length > 0) {
    summaryParts.push(`${changedFields.length} field${changedFields.length > 1 ? 's' : ''} modified`);
  }
  if (hasAddedAttachments) {
    summaryParts.push('attachments to upload');
  }
  if (hasRemovedAttachments) {
    summaryParts.push('attachments to remove');
  }

  return {
    hasChanges,
    changedFields,
    hasAddedAttachments,
    hasRemovedAttachments,
    hasAddedSubItems: false,
    hasRemovedSubItems: false,
    hasReorderedChildren: false,
    summary: summaryParts.join(', '),
  };
}
